/**
 * FoundationTab - Main component for Foundation settings tab
 *
 * Combines:
 * - Core Foundation Questions (who is talking, to whom, why)
 * - POV Preset Selection (intimacy progression)
 * - Six Best Friends (existing implementation)
 *
 * Auto-populate state is now lifted to SongSettingsPanel and passed down
 * via props for centralized management across tabs.
 */

import { useCallback } from 'react'
import { CoreQuestions } from './CoreQuestions'
import { IntimacyExplanation } from './IntimacyExplanation'
import { POVPresetGrid } from './POVPresetGrid'
import { SixBestFriends } from './SixBestFriends'
import type { FoundationTabProps, FoundationQuestions } from './types'

export function FoundationTab({
  settings,
  onSettingsChange
}: FoundationTabProps) {
  // Handler for core questions (nested path updates)
  const handleQuestionUpdate = useCallback(
    (field: keyof FoundationQuestions, value: string | undefined) => {
      onSettingsChange(`foundation_questions.${field}`, value)
    },
    [onSettingsChange]
  )

  // Handler for POV selection
  const handlePOVSelect = useCallback(
    (pov: string) => {
      onSettingsChange('narrative_pov', pov)
    },
    [onSettingsChange]
  )

  // Handler for Six Best Friends nested updates
  const handleSixBestFriendsUpdate = useCallback(
    (field: string, value: string | undefined) => {
      onSettingsChange(`six_best_friends.${field}`, value)
    },
    [onSettingsChange]
  )

  return (
    <div className="space-y-8">
      {/* Core Foundation Questions Section */}
      <section data-tour="core-questions">
        <CoreQuestions
          foundationQuestions={settings.foundation_questions || {}}
          onUpdateQuestion={handleQuestionUpdate}
        />
      </section>

      {/* POV Preset Selection Section */}
      <section data-tour="pov-section">
        <h3 className="text-base font-semibold text-white mb-4">
          Point of View & Intimacy
        </h3>

        <IntimacyExplanation
          title="Intimacy Progression: From Distant Observer to Direct Connection"
          description="Point of view controls the emotional distance between the singer and the listener. Think of it like a camera: wide shots feel distant and observational, while close-ups create intimacy. Choose the perspective that best serves your song's emotional message."
          defaultExpanded={false}
        />

        <POVPresetGrid
          selectedPOV={settings.narrative_pov}
          onSelectPOV={handlePOVSelect}
        />
      </section>

      {/* Six Best Friends Section */}
      <section>
        <h3 className="text-base font-semibold text-white mb-4">Six Best Friends</h3>

        <SixBestFriends
          sixBestFriends={settings.six_best_friends || {}}
          onUpdate={handleSixBestFriendsUpdate}
        />
      </section>
    </div>
  )
}
